import React, { useState } from 'react';
import { Activity, VerificationStatus } from '../types';
import { CheckCircleIcon, XCircleIcon, AlertCircleIcon, ExternalLinkIcon, ClockIcon } from 'lucide-react';
import { SummaryCard } from '../components/SummaryCard';
interface VerificationPageProps {
  activities: Activity[];
  onViewActivity: (id: string) => void;
}
export function VerificationPage({
  activities,
  onViewActivity
}: VerificationPageProps) {
  const [activeTab, setActiveTab] = useState<'Semua' | VerificationStatus>('Belum Diverifikasi');
  // Extract all progress entries
  const allProgress = activities.flatMap(act => (act.progressHistory || []).map(prog => ({
    ...prog,
    activityTitle: act.title,
    activityCode: act.code,
    activityUnit: act.unit
  }))).sort((a, b) => new Date(b.reportedAt).getTime() - new Date(a.reportedAt).getTime());
  // Stats
  const pendingCount = allProgress.filter(p => p.verificationStatus === 'Belum Diverifikasi').length;
  const approvedCount = allProgress.filter(p => p.verificationStatus === 'Disetujui').length;
  const revisionCount = allProgress.filter(p => p.verificationStatus === 'Revisi').length;
  const rejectedCount = allProgress.filter(p => p.verificationStatus === 'Ditolak').length;
  const filteredVerifications = allProgress.filter(item => activeTab === 'Semua' ? true : item.verificationStatus === activeTab);
  const handleAction = (action: string) => {
    alert(`Aksi ${action} akan disimpan ke database pada versi berikutnya.`);
  };
  return <div className="space-y-6 flex flex-col h-full" data-id="element-1096">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1097">
        <h2 className="text-2xl font-bold text-[#E2E8F0] uppercase tracking-wide flex items-center" data-id="element-1098">
          <CheckCircleIcon className="w-6 h-6 mr-3 text-[#FFB800]" data-id="element-1099" />
          VERIFIKASI PROGRES
        </h2>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1100">
        <SummaryCard title="Menunggu Verifikasi" value={pendingCount} icon={<ClockIcon className="w-6 h-6" data-id="element-1102" />} iconBgColor="bg-[#FFB800]/10" iconColor="text-[#FFB800]" borderColor="border-t-[#FFB800]" data-id="element-1101" />
        <SummaryCard title="Disetujui" value={approvedCount} icon={<CheckCircleIcon className="w-6 h-6" data-id="element-1104" />} iconBgColor="bg-[#00FF88]/10" iconColor="text-[#00FF88]" borderColor="border-t-[#00FF88]" data-id="element-1103" />
        <SummaryCard title="Revisi" value={revisionCount} icon={<AlertCircleIcon className="w-6 h-6" data-id="element-1106" />} iconBgColor="bg-[#0EA5E9]/10" iconColor="text-[#0EA5E9]" borderColor="border-t-[#0EA5E9]" data-id="element-1105" />
        <SummaryCard title="Ditolak" value={rejectedCount} icon={<XCircleIcon className="w-6 h-6" data-id="element-1108" />} iconBgColor="bg-[#FF3B3B]/10" iconColor="text-[#FF3B3B]" borderColor="border-t-[#FF3B3B]" data-id="element-1107" />
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-[#1E3A5F] overflow-x-auto pb-px" data-id="element-1109">
        {(['Semua', 'Belum Diverifikasi', 'Disetujui', 'Revisi', 'Ditolak'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 ${activeTab === tab ? 'border-[#00D4FF] text-[#00D4FF] bg-[#00D4FF]/5' : 'border-transparent text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[#111827]'}`} data-id="element-1110">
            {tab}
          </button>)}
      </div>

      <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] overflow-hidden flex-1" data-id="element-1111">
        <div className="overflow-x-auto" data-id="element-1112">
          <table className="w-full text-sm text-left" data-id="element-1113">
            <thead className="text-xs text-[#94A3B8] uppercase bg-[#0F1629] border-b border-[#1E3A5F]" data-id="element-1114">
              <tr data-id="element-1115">
                <th className="px-4 py-3 font-semibold" data-id="element-1116">KEGIATAN</th>
                <th className="px-4 py-3 font-semibold" data-id="element-1117">PELAPOR</th>
                <th className="px-4 py-3 font-semibold" data-id="element-1118">WAKTU LAPORAN</th>
                <th className="px-4 py-3 font-semibold" data-id="element-1119">PROGRES</th>
                <th className="px-4 py-3 font-semibold min-w-[250px]" data-id="element-1120">
                  CATATAN PROGRES
                </th>
                <th className="px-4 py-3 font-semibold text-center" data-id="element-1121">
                  AKSI / STATUS
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1122">
              {filteredVerifications.length > 0 ? filteredVerifications.map(item => {
              const isPending = item.verificationStatus === 'Belum Diverifikasi';
              return <tr key={item.id} className={`border-b border-[#1E3A5F]/50 hover:bg-[#1A2332] transition-colors ${isPending ? 'border-l-2 border-l-[#FFB800] bg-[#FFB800]/5' : 'border-l-2 border-l-transparent'}`} data-id="element-1123">
                      <td className="px-4 py-4" data-id="element-1124">
                        <div className="font-mono text-[#00D4FF] text-xs mb-1" data-id="element-1125">
                          {item.activityCode}
                        </div>
                        <div className="font-medium text-[#E2E8F0] text-sm line-clamp-2" title={item.activityTitle} data-id="element-1126">
                          {item.activityTitle}
                        </div>
                        <div className="text-xs text-[#94A3B8] mt-1" data-id="element-1127">
                          {item.activityUnit}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-[#E2E8F0]" data-id="element-1128">
                        {item.reportedBy}
                      </td>
                      <td className="px-4 py-4 font-mono text-xs text-[#94A3B8]" data-id="element-1129">
                        {new Date(item.reportedAt).toLocaleString('id-ID')}
                      </td>
                      <td className="px-4 py-4" data-id="element-1130">
                        <span className="font-mono text-[#00D4FF] font-bold text-sm glow-cyan" data-id="element-1131">
                          {item.progressPercent}%
                        </span>
                      </td>
                      <td className="px-4 py-4" data-id="element-1132">
                        <p className="text-sm text-[#E2E8F0] line-clamp-2" data-id="element-1133">
                          {item.progressNote}
                        </p>
                        {item.obstacleNote && item.obstacleNote !== 'Nihil.' && <p className="text-xs text-[#FF3B3B] mt-1 flex items-center" data-id="element-1134">
                              <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-1135" />{' '}
                              Kendala dilaporkan
                            </p>}
                      </td>
                      <td className="px-4 py-4" data-id="element-1136">
                        {isPending ? <div className="flex flex-col space-y-2" data-id="element-1137">
                            <div className="flex space-x-2 justify-center" data-id="element-1138">
                              <button onClick={() => handleAction('Setujui')} className="p-1.5 bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/30 rounded hover:bg-[#00FF88] hover:text-[#0B0F1A] transition-colors" title="Setujui" data-id="element-1139">
                                <CheckCircleIcon className="w-4 h-4" data-id="element-1140" />
                              </button>
                              <button onClick={() => handleAction('Revisi')} className="p-1.5 bg-[#FFB800]/10 text-[#FFB800] border border-[#FFB800]/30 rounded hover:bg-[#FFB800] hover:text-[#0B0F1A] transition-colors" title="Minta Revisi" data-id="element-1141">
                                <AlertCircleIcon className="w-4 h-4" data-id="element-1142" />
                              </button>
                              <button onClick={() => handleAction('Tolak')} className="p-1.5 bg-[#FF3B3B]/10 text-[#FF3B3B] border border-[#FF3B3B]/30 rounded hover:bg-[#FF3B3B] hover:text-[#0B0F1A] transition-colors" title="Tolak" data-id="element-1143">
                                <XCircleIcon className="w-4 h-4" data-id="element-1144" />
                              </button>
                            </div>
                            <button onClick={() => onViewActivity(item.activityId)} className="flex items-center justify-center text-xs text-[#00D4FF] hover:underline mt-1" data-id="element-1145">
                              <ExternalLinkIcon className="w-3 h-3 mr-1" data-id="element-1146" />{' '}
                              Detail
                            </button>
                          </div> : <div className="text-center" data-id="element-1147">
                            <span className={`inline-block px-2 py-1 rounded text-xs font-bold uppercase tracking-wider border ${item.verificationStatus === 'Disetujui' ? 'bg-[#00FF88]/10 text-[#00FF88] border-[#00FF88]/30 glow-green' : item.verificationStatus === 'Revisi' ? 'bg-[#0EA5E9]/10 text-[#0EA5E9] border-[#0EA5E9]/30' : 'bg-[#FF3B3B]/10 text-[#FF3B3B] border-[#FF3B3B]/30 glow-red'}`} data-id="element-1148">
                              {item.verificationStatus}
                            </span>
                            {item.verifiedBy && <div className="text-[10px] text-[#94A3B8] mt-2" data-id="element-1149">
                                Oleh: {item.verifiedBy}
                              </div>}
                          </div>}
                      </td>
                    </tr>;
            }) : <tr data-id="element-1150">
                  <td colSpan={6} className="px-4 py-12 text-center text-[#94A3B8]" data-id="element-1151">
                    <CheckCircleIcon className="w-12 h-12 text-[#00FF88]/30 mx-auto mb-3" data-id="element-1152" />
                    <p className="text-sm" data-id="element-1153">
                      Tidak ada data yang sesuai dengan filter.
                    </p>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}